import { priceChannelFlags } from "@/services/price-channel/feature-flags";

const fixtureMode = priceChannelFlags.readSource === "fixture";
const shell = fixtureMode
  ? () => import("@/views/pc/pricechannel/index.vue")
  : () => import("@/views/pc/pricechannel/ApiPriceChannelShell.vue");
const apiWorkspace = () => import("@/views/pc/pricechannel/display/ApiMarketWorkspace.vue");
const fixtureMarketGuard = (to, from, next) =>
  import("@/utils/pricechannel/scope/scope-routes")
    .then(({ applyProfessionalMarketRoute }) => applyProfessionalMarketRoute(to, from, next))
    .catch(next);
const fixtureInstrument = () =>
  import("@/utils/pricechannel/navigation/navigation-preload")
    .then(({ preloadInstrumentDetailView }) => preloadInstrumentDetailView());
const publicMeta = (title) => ({
  title,
  priceDomain: "public-trend",
  showSearch: false,
  showUserInfo: true,
  hideLayoutTop: fixtureMode,
});
const personalMeta = (title) => ({
  ...publicMeta(title),
  type: "my",
  priceDomain: "business",
  requiresAuth: true,
});

/**
 * 挂入H5中央PC Router，不创建第二个Vue实例。旧深链保留原路径，
 * 新逻辑Scope和产品路径按replace归一到当前页入口。
 */
const publicRoutes = [
  {
    path: "pricechannel",
    component: shell,
    meta: publicMeta("价格指数"),
    children: [
      {
        path: "display",
        name: "pricechannel-display",
        component: fixtureMode ? () => import("@/views/pc/pricechannel/display/index.vue") : () => import("@/views/pc/pricechannel/display/ApiMarketHome.vue"),
        meta: publicMeta("价格指数"),
      },
      {
        path: "display/market",
        name: "pricechannel-display-market",
        component: fixtureMode ? () => import("@/views/pc/pricechannel/display/Market.vue") : apiWorkspace,
        beforeEnter: fixtureMode ? fixtureMarketGuard : undefined,
        meta: publicMeta("分类行情"),
      },
      {
        path: "display/watchlist",
        name: "pricechannel-display-watchlist",
        component: fixtureMode
          ? () => import("@/views/pc/pricechannel/display/Watchlist.vue")
          : () => import("@/views/pc/pricechannel/display/ApiWatchlist.vue"),
        meta: { ...publicMeta("我的自选"), requiresAuth: true },
      },
      {
        path: "display/instruments/:instrumentId",
        name: "pricechannel-display-instrument",
        component: fixtureMode ? fixtureInstrument : apiWorkspace,
        meta: publicMeta("具体产品行情"),
      },
      {
        path: "instrument/:instrumentId",
        redirect: (to) => ({
          path: "/pc/pricechannel/display/instruments/" + to.params.instrumentId,
          query: to.query,
          replace: true,
        }),
      },
      {
        path: "scope/:scopeId",
        redirect: (to) => ({
          path: "/pc/pricechannel/display/market",
          query: { ...to.query, scope: to.params.scopeId },
          replace: true,
        }),
      },
    ],
  },
];

const fixturePersonalRoutes = [{
    path: "user/pricechannel",
    component: shell,
    meta: personalMeta("价格频道"),
    children: [
      { path: "", name: "my-pricechannel", component: () => import("@/views/pc/my/pricechannel/MessageCenter.vue"), meta: personalMeta("消息中心") },
      { path: "messages", redirect: "/pc/user/pricechannel" },
      { path: "certification", name: "my-pricechannel-certification", component: () => import("@/views/pc/my/pricechannel/EnterpriseCertification.vue"), meta: personalMeta("企业认证") },
      { path: "company", name: "my-pricechannel-company", component: () => import("@/views/pc/my/pricechannel/vendor/CompanyProfile.vue"), meta: personalMeta("本企业资料") },
      { path: "qualifications", name: "my-pricechannel-qualifications", component: () => import("@/views/pc/my/pricechannel/vendor/QualificationLibrary.vue"), meta: personalMeta("认证材料") },
      { path: "company-preview", name: "my-pricechannel-company-preview", component: () => import("@/views/pc/my/pricechannel/vendor/PublicProfilePreview.vue"), meta: personalMeta("企业公开档案") },
      { path: "products", name: "my-pricechannel-products", component: () => import("@/views/pc/my/pricechannel/vendor/ProductSubmission.vue"), meta: personalMeta("产品上报") },
      { path: "quote-intakes", name: "my-pricechannel-quote-intakes", component: () => import("@/views/pc/my/pricechannel/QuoteInventory.vue"), meta: personalMeta("料号上报") },
      { path: "submissions", name: "my-pricechannel-submissions", component: () => import("@/views/pc/my/pricechannel/CatalogSubmissions.vue"), meta: personalMeta("提交历史") },
      { path: "operations-reviews", name: "my-pricechannel-operations-reviews", component: () => import("@/views/pc/my/pricechannel/OperationsReviews.vue"), meta: personalMeta("运营审核") },
      { path: "results", name: "my-pricechannel-review-results", component: () => import("@/views/pc/my/pricechannel/ReviewResults.vue"), meta: personalMeta("审查结果") },
      { path: "submissions/:submissionId", name: "my-pricechannel-submission-detail", component: () => import("@/views/pc/my/pricechannel/vendor/SubmissionDetail.vue"), meta: personalMeta("提交详情") },
      {
        path: "results/certifications/:certificationId",
        redirect: (to) => ({
          path: "/pc/user/pricechannel/results",
          query: { type: "certification", result: to.params.certificationId },
          replace: true,
        }),
      },
      {
        path: "results/products/:submissionId",
        redirect: (to) => ({
          path: "/pc/user/pricechannel/results",
          query: { type: "product", result: to.params.submissionId },
          replace: true,
        }),
      },
    ],
  }];

// 正式模式使用API公共页及已接Spring的业务页；旧深链归一到正式入口，不能落回Fixture。
export const pcPriceChannelRoutes = fixtureMode
  ? [...publicRoutes, ...fixturePersonalRoutes]
  : [
      ...publicRoutes,
      {
        path: "user/pricechannel",
        redirect: "/pc/my/pricechannel/activity",
      },
      {
        path: "user/pricechannel/messages",
        redirect: "/pc/my/pricechannel/activity",
      },
      {
        path: "user/pricechannel/certification",
        redirect: "/pc/my/pricechannel/certifications",
      },
      ...["company", "qualifications", "company-preview"].map((surface) => ({
        path: `user/pricechannel/${surface}`,
        redirect: `/pc/my/pricechannel/${surface}`,
      })),
      {
        path: "user/pricechannel/submissions",
        redirect: "/pc/my/pricechannel/submissions",
      },
      {
        path: "user/pricechannel/products",
        redirect: "/pc/my/pricechannel/submissions",
      },
      {
        path: "user/pricechannel/quote-intakes",
        redirect: "/pc/my/pricechannel/submissions",
      },
      {
        path: "user/pricechannel/operations-reviews",
        redirect: "/pc/my/pricechannel/reviews",
      },
      {
        path: "user/pricechannel/results",
        redirect: (to) => ({ path: "/pc/my/pricechannel/results", query: to.query }),
      },
      {
        path: "user/pricechannel/results/certifications/:certificationId",
        redirect: (to) => ({ path: "/pc/my/pricechannel/results", query: { ...to.query, type: "certification", result: to.params.certificationId } }),
      },
      {
        path: "user/pricechannel/results/products/:submissionId",
        redirect: (to) => ({ path: "/pc/my/pricechannel/results", query: { ...to.query, type: "product", result: to.params.submissionId } }),
      },
      {
        path: "user/pricechannel/results/part-numbers/:submissionId",
        redirect: (to) => ({ path: "/pc/my/pricechannel/results", query: { ...to.query, type: "part-number", result: to.params.submissionId } }),
      },
      {
        path: "user/pricechannel/submissions/:submissionId",
        redirect: (to) => ({ path: "/pc/my/pricechannel/submissions", query: { submission: to.params.submissionId } }),
      },
      {
        path: "user/pricechannel/*",
        redirect: "/pc/my/pricechannel",
      },
    ];
